import { useState, useEffect, useRef } from 'react';
import FileIcon from '@app/assets/icons/file.svg?react';
import PlusWhiteIcon from '@app/assets/icons/plus-white.svg?react';
import EditIcon from '@app/assets/icons/edit-03.svg?react';
import TrashRedIcon from '@app/assets/icons/trash-red.svg?react';
import BookIcon from '@app/assets/icons/book-03.svg?react';
import DotsGrayIcon from '@app/assets/icons/dots-gray.svg?react';
import { getTimeAgo } from '@app/utils/utils';

export interface FeedbackNote {
  id: string;
  description: string;
  createdAt: string;
  createdBy: string;
}

interface FeedbackNotesProps {
  notes: FeedbackNote[];
  onAddNote?: (description: string) => void;
  onEditNote?: (noteId: string, description: string) => void;
  onDeleteNote?: (noteId: string) => void;
  isLoading?: boolean;
  isCreating?: boolean;
  isUpdating?: boolean;
  isDeleting?: boolean;
  currentUserName?: string;
}

export const FeedbackNotes = ({
  notes,
  onAddNote,
  onEditNote,
  onDeleteNote,
  isLoading = false,
  isCreating = false,
  isUpdating = false,
  isDeleting = false,
  currentUserName = 'Unknown User',
}: FeedbackNotesProps) => {
  const [newNoteContent, setNewNoteContent] = useState('');
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState('');
  const [showMenuId, setShowMenuId] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const menuRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedElement = event.target as Node;
      const isClickInsideAnyMenu = Object.values(menuRefs.current).some(
        (ref) => ref && ref.contains(clickedElement)
      );

      if (!isClickInsideAnyMenu) {
        setShowMenuId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      // Clean up refs
      menuRefs.current = {};
    };
  }, []);

  // Close modal when clicking outside
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showAddModal) {
        handleCloseModal();
      }
    };

    if (showAddModal) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [showAddModal]);

  const handleAddNote = () => {
    if (newNoteContent.trim() && onAddNote) {
      onAddNote(newNoteContent.trim());
      setNewNoteContent('');
      setShowAddModal(false);
    }
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setNewNoteContent('');
  };

  const handleKeyDown = (
    e: React.KeyboardEvent,
    action: () => void,
    cancelAction: () => void
  ) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      action();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelAction();
    }
  };

  const handleEditNote = () => {
    if (editingContent.trim() && editingNoteId && onEditNote) {
      onEditNote(editingNoteId, editingContent.trim());
      setEditingNoteId(null);
      setEditingContent('');
    }
  };

  const handleDeleteNote = (noteId: string) => {
    if (onDeleteNote) {
      onDeleteNote(noteId);
      setShowMenuId(null);
    }
  };

  const startEditing = (note: FeedbackNote) => {
    setEditingNoteId(note.id);
    setEditingContent(note.description);
    setShowMenuId(null);
  };

  const cancelEditing = () => {
    setEditingNoteId(null);
    setEditingContent('');
  };

  return (
    <div className="mt-6 p-4 bg-gray-50 rounded-lg">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-base text-gray-900">Notes</h3>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center justify-center w-8 h-8 bg-brand-baseBlue rounded-md hover:bg-brand-mediumBlue1 transition-colors"
          aria-label="Add note"
        >
          <PlusWhiteIcon width={10} height={10} />
        </button>
      </div>

      {/* Notes List */}
      {isLoading ? (
        <div className="text-center py-8 text-gray-500">
          <p className="text-sm">Loading notes...</p>
        </div>
      ) : (
        <div className="space-y-3">
          {notes.map((note) => (
            <div key={note.id} className="relative p-3 bg-white rounded-lg">
              {editingNoteId === note.id ? (
                // Edit Mode
                <div>
                  <textarea
                    value={editingContent}
                    onChange={(e) => setEditingContent(e.target.value)}
                    onKeyDown={(e) =>
                      handleKeyDown(e, handleEditNote, cancelEditing)
                    }
                    className="w-full p-2 border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-brand-baseBlue focus:border-transparent"
                    rows={3}
                    autoFocus
                  />
                  <div className="flex justify-end gap-2 mt-2">
                    <button
                      onClick={cancelEditing}
                      className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleEditNote}
                      disabled={!editingContent.trim() || isUpdating}
                      className="px-3 py-1 text-sm bg-brand-baseBlue text-white rounded-md hover:bg-brand-mediumBlue1 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isUpdating ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              ) : (
                // View Mode
                <>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-1">
                      <FileIcon
                        width={16}
                        height={16}
                        className="text-gray-500"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-700 leading-relaxed">
                        {note.description}
                      </p>
                      <div className="flex justify-end mt-2">
                        <p className="text-xs text-gray-500">
                          {getTimeAgo(new Date(note.createdAt))} | by{' '}
                          {note.createdBy || currentUserName}
                        </p>
                      </div>
                    </div>
                    <div className="relative flex-shrink-0">
                      <button
                        onClick={() =>
                          setShowMenuId(showMenuId === note.id ? null : note.id)
                        }
                        className="px-2 py-3 bg-[#F6F7FA] rounded transition-colors"
                        aria-label="More options"
                      >
                        <DotsGrayIcon width={12} height={4} />
                      </button>

                      {/* Dropdown Menu */}
                      {showMenuId === note.id && (
                        <div
                          ref={(el) => {
                            menuRefs.current[note.id] = el;
                          }}
                          className="absolute right-0 top-8 z-10 bg-white border border-gray-200 rounded-md shadow-lg py-1 min-w-[150px]"
                        >
                          <button
                            onClick={() => startEditing(note)}
                            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            <EditIcon width={14} height={14} />
                            Edit note
                          </button>
                          <button
                            onClick={() => handleDeleteNote(note.id)}
                            disabled={isDeleting}
                            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <TrashRedIcon width={14} height={14} />
                            {isDeleting ? 'Deleting...' : 'Delete note'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add Note Modal */}
      {showAddModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={handleCloseModal}
        >
          <div
            className="bg-white rounded-lg w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-start justify-between p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#F6F7FA] rounded-md">
                  <BookIcon
                    width={25}
                    height={25}
                    className="text-base-davyGray"
                  />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-gray-900">Add note</h3>
                  <p className="text-sm text-gray-600">
                    Add additional information about the feedback
                  </p>
                </div>
              </div>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close modal"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M18 6L6 18M6 6L18 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
                              <textarea
                  value={newNoteContent}
                  onChange={(e) => setNewNoteContent(e.target.value)}
                  onKeyDown={(e) =>
                    handleKeyDown(e, handleAddNote, handleCloseModal)
                  }
                  placeholder="Problem description and solution"
                  className="w-full p-3 rounded-md bg-[#F8F9FB] resize-none focus:outline-none focus:ring-2 focus:ring-brand-baseBlue focus:border-transparent"
                  rows={4}
                  autoFocus
                />
            </div>

            {/* Footer */}
            <div className="flex gap-4 p-6">
              <button
                onClick={handleCloseModal}
                className="flex-1 px-6 py-3 text-base font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddNote}
                disabled={!newNoteContent.trim() || isCreating}
                className="flex-1 px-6 py-3 text-base font-medium text-white bg-brand-baseBlue rounded-md hover:bg-brand-mediumBlue1 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isCreating ? 'Saving...' : 'Save note'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
